import { SELECTED_PRODUCTS_KEY } from "@constants/storageKeys"
import { useMarketPricing } from "@hooks/useMarketPricing"
import { InventoryData } from "@hooks/useMultiWarehouseInventory"
import { assignPriorityBasedOnProductName } from "@utils/assignPriorityBasedOnProductName"
import { assignSubtypePriority } from "@utils/assignSubtypePriority"
import getAvailabilityByRegion from "@utils/getAvailabilityByRegion"
import { formatContentfulVariant, getIn } from "@utils/index"
import { getMarketAmount } from "@utils/pricing"
import { getLocalStorage, writeLocalStorage } from "@utils/storage"
import atob from "atob"
import * as R from "ramda"
import React from "react"

type Args = {
  allProducts: any[]
  automaticDiscounts?: any[]
  customerCountryCode?: string
  customerLocationFetched?: boolean
  globalData?: any
  inventoryData: InventoryData
  mode: "Products" | "SpecialOps"
  onClickProductCard: (product: any) => void
  onSelectItem: (product: any, list: any) => void
  pageContext: any
}

/**
 * Formats raw Contentful products for display on collection pages
 * Handles:
 * - Variant formatting
 * - Availability by region
 * - GTM/Elevar product data
 * - Discount tags
 * - Priority assignments
 */
export function useFormattedCollectionProducts({
  allProducts,
  automaticDiscounts = [],
  customerCountryCode,
  customerLocationFetched,
  globalData,
  inventoryData,
  mode,
  onClickProductCard,
  onSelectItem,
  pageContext,
}: Args) {
  // Collect all variant SKUs for market pricing fetch
  const allVariantSkus = React.useMemo(() => {
    const skus: string[] = []
    allProducts.forEach((product) => {
      const variants = getIn(product, "variants", [])
      variants.forEach((variant) => {
        if (variant.sku) {
          skus.push(variant.sku)
        }
      })
    })
    return skus
  }, [allProducts])

  // Fetch market pricing data for all variants
  const { data: marketPricingData } = useMarketPricing(allVariantSkus)

  // Create pricing map for quick lookups
  const marketPricingMap = React.useMemo(() => {
    const map = new Map()
    marketPricingData.forEach((variant) => {
      map.set(variant.sku, variant.marketPrices)
    })
    return map
  }, [marketPricingData])

  // Merge market pricing into products
  const productsWithPricing = React.useMemo(() => {
    return allProducts.map((product) => ({
      ...product,
      variants: (getIn(product, "variants", []) || []).map((variant) => ({
        ...variant,
        marketPrices: marketPricingMap.get(variant.sku) || {},
      })),
    }))
  }, [allProducts, marketPricingMap])

  const formattedProducts = React.useMemo(() => {
    return R.pipe(
      (products) => {
        return products
          .filter(
            (product) => !!product.variants && product.variants.length > 0
          )
          .map((product, i) => {
            const shopifyProductId = getIn(
              product,
              "shopifyProduct.shopifyProductId",
              ""
            )

            const allVariants = getIn(product, "variants", [])
            let variants = []

            if (!!product.customPrint && pageContext.deviceSlug) {
              variants = pageContext.deviceSlug
                ? allVariants.filter(
                    ({ device }) =>
                      device && device.slug === pageContext.deviceSlug
                  )
                : allVariants
            } else {
              variants = getIn(product, "variants", [])
            }

            const formattedVariants = variants.map((variant) => {
              const formattedVariant: any = formatContentfulVariant(variant)
              let isAvailable = false

              if (
                customerLocationFetched &&
                customerCountryCode &&
                inventoryData
              ) {
                if (mode === "SpecialOps") {
                  // SpecialOps mode - check availability with variant availability field
                  if (
                    customerCountryCode &&
                    customerCountryCode !== "US" &&
                    ["Sell Everywhere", "Sell INTL"].includes(
                      formattedVariant.availability
                    )
                  ) {
                    isAvailable = getAvailabilityByRegion(
                      customerCountryCode,
                      formattedVariant.sku,
                      inventoryData,
                      { mode: "SOPDP" }
                    ).isAvailable
                  } else if (
                    ["Sell Everywhere", "Sell in US"].includes(
                      formattedVariant.availability
                    )
                  ) {
                    isAvailable = getAvailabilityByRegion(
                      "US",
                      formattedVariant.sku,
                      inventoryData,
                      { mode: "SOPDP" }
                    ).isAvailable
                  }
                } else {
                  // Products mode - standard availability check
                  if (!product.customPrint) {
                    isAvailable = getAvailabilityByRegion(
                      customerCountryCode,
                      formattedVariant.sku,
                      inventoryData
                    ).isAvailable
                  }
                }
              }

              formattedVariant.isUnavailable = !isAvailable
              return formattedVariant
            })

            let multipleDevices
            if (product.devices) {
              if (pageContext.deviceSlug) {
                const selectedDevice = product.devices.find(
                  ({ slug }) => slug === pageContext.deviceSlug
                )
                multipleDevices = selectedDevice?.name || ""
              } else {
                multipleDevices = product.devices.reduce((acc, curr) => {
                  if (acc) {
                    acc = acc + `, ${curr.name}`
                  } else {
                    acc = curr.name
                  }

                  return acc
                }, undefined)
              }
            }

            const gtmProduct = {
              brand: product.brand.name,
              device: multipleDevices ? multipleDevices : product.device.name,
              deviceType: product.device.type.name,
              list: `Collection: ${
                pageContext.seoHeader ||
                pageContext.seoTitle ||
                pageContext.title
              }`,
              price: getMarketAmount(
                product.variants[0],
                customerCountryCode || "US",
                "price"
              ),
              productId: shopifyProductId,
              productName: product.productName,
              sku: product.variants[0].sku,
              variantName: product.variants[0].name,
            }

            let list = ""

            if (typeof window !== "undefined") {
              list = getIn(window, "location.pathname")
            }

            const elevarProduct = {
              brand: "Urban Armor Gear",
              device: multipleDevices ? multipleDevices : product.device.name,
              deviceType: product.device.type.name,
              list,
              position: i + 1,
              price: getMarketAmount(
                product.variants[0],
                customerCountryCode || "US",
                "price"
              ),
              productId: shopifyProductId,
              productName: product.productName,
              sku: product.variants[0].sku,
              variantId: product.variants[0].shopifyVariantId,
              variantName: product.variants[0].name,
            }

            let discountTag
            let encodedProductId
            try {
              encodedProductId = atob(shopifyProductId)
            } catch {
              encodedProductId = shopifyProductId
            }

            if (automaticDiscounts?.length) {
              const xIndex = automaticDiscounts.findIndex(
                ({ customerBuysProducts }) =>
                  customerBuysProducts?.indexOf(encodedProductId) > -1
              )
              const yIndex = automaticDiscounts.findIndex(
                ({ customerGetsProducts }) =>
                  customerGetsProducts?.indexOf(encodedProductId) > -1
              )

              let discountConfig
              if (xIndex > -1 || yIndex > -1) {
                const discountIndex = xIndex > -1 ? xIndex : yIndex
                const discountId = automaticDiscounts[discountIndex].id
                const parsedDiscountId = discountId.replace(
                  "gid://shopify/DiscountAutomaticNode/",
                  ""
                )
                discountConfig = globalData?.automaticDiscounts?.find(
                  ({ discountId }) => discountId === parsedDiscountId
                )
              }

              let tagData
              if (discountConfig) {
                if (xIndex > -1) {
                  tagData = discountConfig.tagX
                } else if (yIndex > -1) {
                  tagData = discountConfig.tagY
                }
              }

              if (tagData) {
                // hide discountTag for now
                // discountTag = {
                //   text: tagData.text,
                //   mobileText: tagData.mobileText,
                //   color: tagData.color,
                //   backgroundColor: tagData.backgroundColor,
                // }
              }
            }

            const tags =
              getIn(product, "tags") || getIn(product, "series.tags") || []
            const formattedTags = tags.map(({ icon, ...rest }) => ({
              icon: icon?.file?.url,
              ...rest,
            }))

            const baseProduct = {
              ...R.pick([
                "createdAt",
                "customPrint",
                "bundleProduct",
                "slug",
                "productName",
                "brand",
                "device",
                "devices",
                "builtInMagnet",
                "series",
              ])(product),
              elevarProduct,
              gtmProduct,
              images: getIn(product, "images", []).map(
                (image) => image?.file?.url
              ),
              onClick: () => {
                const selectedProducts =
                  getLocalStorage(SELECTED_PRODUCTS_KEY) || {}
                writeLocalStorage(SELECTED_PRODUCTS_KEY, {
                  ...selectedProducts,
                  [elevarProduct.productId]: elevarProduct.list,
                })

                onClickProductCard(gtmProduct)
                onSelectItem(elevarProduct, elevarProduct.list)
              },
              productSubType: getIn(product, "productSubType.name"),
              shopifyProductId: getIn(
                product,
                "shopifyProduct.shopifyProductId"
              ),
              tags: discountTag
                ? [discountTag, ...formattedTags]
                : formattedTags,
              variants: formattedVariants,
            }

            // Add customPrintCollection for SpecialOps
            if (mode === "SpecialOps") {
              return {
                ...baseProduct,
                customPrintCollection: getIn(
                  product,
                  "customPrintCollection.name",
                  "Other"
                ),
              }
            }

            return baseProduct
          })
      },
      // Manually assigning priority value based on product name. Ideally value would come from Contentful field
      R.map(assignPriorityBasedOnProductName),
      R.map(assignSubtypePriority)
    )(productsWithPricing)
  }, [
    productsWithPricing,
    inventoryData,
    automaticDiscounts,
    customerLocationFetched,
    customerCountryCode,
    mode,
    pageContext,
    globalData,
    onClickProductCard,
    onSelectItem,
  ])

  return formattedProducts
}
